import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ButtonLoader,
  DualRangeSlider,
  LoadingScreen,
  MultiCheckDropdown,
} from "Components";
import { PrivateNotes } from "./PrivateNotes";
import { Notifications } from "./Notifications";
import { newAuthService } from "Service/Axios";
import useAuth from "Context/useAuth";
import {
  GLOBAL_SEARCH_URL,
  getGlobalASFilterDetailsUrl,
  getGlobalASFilterListUrl,
  getGlobalASFilterResultUrl,
  getGlobalASSearchListUrl,
  getGlobalNotificationCountApi,
} from "Constant/api";
import { DEFAULT_PAGE_SIZE, USER_TYPE } from "Constant/constants";
import { paths } from "Constant/paths";
import { useGetGreetMessage, useOutsideClick } from "Utils/hooks";
import { disablePastDate, getFirstLetterOfName } from "Utils";

const PrivateHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    isSiderbarToggle,
    toggleSidebar,
    triggerGlobalSearchReset,
    Logout,
  } = useAuth();

  const message = useGetGreetMessage();
  const { search: searchParams } = useLocation();

  const searchDropdownRef = useRef(null);

  const [notificationDropdown, setNotificationDropdown] = useState(false);
  const [privateNoteDropdown, setPrivateNoteDropdown] = useState(false);
  const [globalSearchDropdown, setGlobalSearchDropdown] = useState(false);
  const [advanceSearchDropdown, setAdvanceSearchDropdown] = useState(false);
  const [isAdvanceSearchApplied, setIsAdvanceSearchApplied] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [customFilterLoading, setCustomFilterLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [totalNotification, setTotalNotification] = useState(0);
  const [searchResult, setSearchResult] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isAllMatched, setIsAllMatched] = useState(false);
  const [matchValue, setMatchValue] = useState("All");
  const [advanceSearchType, setAdvanceSearchType] = useState("");
  const [customFilterSearchText, setCustomFilterSearchText] = useState("");
  const [searchList, setSearchList] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [customFilterList, setCustomFilterList] = useState([]);

  // Toggle
  const showAdvanceSearchDropdown = () => {
    document.body.classList.add("show-search-wrapper");
    setGlobalSearchDropdown(false);
    setAdvanceSearchDropdown(true);
  };

  const closeSearchDropdown = () => {
    document.body.classList.remove("show-search-wrapper");
    setAdvanceSearchDropdown(false);
    setGlobalSearchDropdown(false);
    setSearchList([]);
    setAdvanceSearchType("");
    setFilterList([]);
  };

  useOutsideClick(searchDropdownRef, closeSearchDropdown);

  const toggleNotificationDropdown = () => {
    setNotificationDropdown(!notificationDropdown);
  };

  const togglePrivateNoteDropdown = () => {
    setPrivateNoteDropdown(!privateNoteDropdown);
  };

  // Search
  const getGlobalSearchResult = async () => {
    const paload = {
      searchtext: searchText,
      limit: "10",
    };
    const response = await newAuthService(GLOBAL_SEARCH_URL, "POST", paload);
    setSearchResult(response.data);
    setGlobalSearchDropdown(true);
  };

  useEffect(() => {
    if (
      !advanceSearchDropdown &&
      !isAdvanceSearchApplied &&
      searchText.trim()
    ) {
      const delayDebounceFn = setTimeout(() => {
        getGlobalSearchResult();
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setSearchResult(null);
    }
  }, [searchText, advanceSearchDropdown, isAdvanceSearchApplied]);

  // Listings
  const customFilterListWithSearch = useMemo(() => {
    if (
      customFilterList &&
      customFilterList.length > 0 &&
      customFilterSearchText.length > 0
    ) {
      return customFilterList.filter((filter) =>
        filter.fieldName
          .toLowerCase()
          .includes(customFilterSearchText.toLowerCase())
      );
    }
    return customFilterList;
  }, [customFilterList, customFilterSearchText]);

  const fetchNotificationCount = async () => {
    try {
      const data = {};
      const response = await newAuthService(
        getGlobalNotificationCountApi,
        "POST",
        data
      );
      if (response.status) {
        setTotalNotification(response.data);
      } else {
        setTotalNotification(0);
      }
    } catch (e) {
      toast.error(t("common.messages.notification.failed.unauthorized"));
    }
  };

  useEffect(() => {
    fetchNotificationCount();
  }, []);

  const fetchAdvanceSearchTypeList = async () => {
    if (!searchList.length) {
      setSearchLoading(true);
      const payload = {};
      const response = await newAuthService(
        getGlobalASSearchListUrl,
        "POST",
        payload
      );
      setSearchList(response.data);
      setSearchLoading(false);
    }
  };

  const fetchAdvanceSearchFilterList = async () => {
    if (!filterList.length) {
      setFilterLoading(true);
      const payload = {};
      const response = await newAuthService(
        getGlobalASFilterListUrl,
        "POST",
        payload
      );
      const defaultFilters = response.data.fields.map((filter) => ({
        ...filter,
        isPredefined: true,
      }));
      setFilterList(defaultFilters);
      setCustomFilterList(response.data.customFields);
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    const isAdvanceSearchApplied = new URLSearchParams(searchParams).get("ads");
    const serachObject = localStorage.getItem("serachObject");

    if (isAdvanceSearchApplied === "1" && serachObject) {
      const decodedSerachObject = JSON.parse(decodeURIComponent(serachObject));
      setSearchText(decodedSerachObject.search.searchText);
      setAdvanceSearchType(decodedSerachObject.search.searchType);
      setFilterList(decodedSerachObject.filterList);
      setIsAdvanceSearchApplied(true);
    } else {
      setIsAdvanceSearchApplied(false);
    }

    if (advanceSearchDropdown) {
      fetchAdvanceSearchTypeList();
      if (!isAdvanceSearchApplied || !serachObject) {
        fetchAdvanceSearchFilterList();
      }
    }
  }, [searchParams, advanceSearchDropdown]);

  // On Change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    if (!advanceSearchDropdown) {
      setIsAdvanceSearchApplied(false);
    }
  };

  const handleAdvanceSearchType = (gid) => {
    setAdvanceSearchType(gid);
  };

  const handleIsAllMatchedChange = (e) => {
    setIsAllMatched(e.target.checked);
  };

  const handleMatchValueChange = (e) => {
    setMatchValue(e.target.value);
  };

  const handleFilterOperatorChange = (e, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          operatorError: "",
          operator: e.target.value,
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleFilterTextChange = (e, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          values: e.target.value,
          valuesError: "",
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleFilterDateChange = (startDate, endDate, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          values: {
            startDate,
            endDate,
          },
          valuesError: "",
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleFilterSelectChange = (e, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          values: e.target.value,
          valuesError: "",
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleFilterMultiSelectChange = (values, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          values: values,
          valuesError: "",
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleFilterSliderChange = (value, filterId) => {
    const updatedFilterList = filterList.map((filter) => {
      if (filter.id === filterId) {
        return {
          ...filter,
          values: {
            lowestNo: value[0],
            highestNo: value[1],
          },
          valuesError: "",
        };
      }
      return filter;
    });
    setFilterList(updatedFilterList);
  };

  const handleChangeCustomFilter = async (filter) => {
    setCustomFilterLoading(true);
    const payload = {
      questionId: filter.id,
    };
    const response = await newAuthService(
      getGlobalASFilterDetailsUrl,
      "POST",
      payload
    );
    if (response.status) {
      const updatedFilter = {
        ...filter,
        ...response.data,
      };
      setFilterList((prevFilterList) => [...prevFilterList, updatedFilter]);
      setCustomFilterList((prevFilterList) =>
        prevFilterList.filter((f) => f.id !== filter.id)
      );
      setCustomFilterLoading(false);
    } else {
      toast.error(t("common.messages.notification.failed.serverError"));
      setCustomFilterLoading(false);
    }
  };

  const handleCustomFilterSearch = (e) => {
    setCustomFilterSearchText(e.target.value);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilterList = filterList.filter(
      (filter) => filter.id !== filterId
    );
    setFilterList(updatedFilterList);
    const removedFilter = filterList.find((filter) => filter.id === filterId);
    setCustomFilterList((prevFilterList) => [
      ...prevFilterList,
      {
        id: removedFilter.id,
        fieldName: removedFilter.fieldName,
        type: removedFilter.type,
        dropdownValue: [],
      },
    ]);
  };

  // Utils
  const convertToFilters = (filters) => {
    const newArr = [];

    filters.forEach((filter) => {
      const { type: designType, id: questionId, values, operator } = filter;
      const isValueExists = values !== null && values !== undefined;

      if (isValueExists && designType === "text") {
        newArr.push({
          questionId: questionId,
          operator: operator,
          values: [
            {
              key: values,
              value: values,
            },
          ],
        });
      }

      if (isValueExists && designType === "date") {
        newArr.push({
          questionId: questionId,
          operator: operator,
          values: [
            {
              startDate: values?.startDate || "",
              endDate: values?.endDate || "",
            },
          ],
        });
      }

      if (isValueExists && designType === "number") {
        newArr.push({
          questionId: questionId,
          operator: operator,
          values: [
            {
              lowestNo: values.lowestNo,
              highestNo: values.highestNo,
            },
          ],
        });
      }

      if (isValueExists && designType === "select") {
        newArr.push({
          questionId: questionId,
          operator: operator,
          values: [
            {
              key: values,
              value: values,
            },
          ],
        });
      }

      if (isValueExists && designType === "multiselect") {
        const updatedValues =
          values.length > 0 &&
          values.map((v) => ({ key: v.value, value: v.value }));
        newArr.push({
          questionId: questionId,
          operator: operator,
          values: updatedValues,
        });
      }
    });

    return newArr;
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      const updatedFilterList = filterList.map((filter) => {
        let valuesError = "";
        let operatorError = "";

        if (filter.values) {
          const isOperatorValid = filter.operator?.trim();
          const isValuesValid =
            typeof filter.values === "string"
              ? filter.values.trim()
              : Array.isArray(filter.values)
              ? filter.values.length
              : filter.values.startDate ||
                filter.values.endDate ||
                filter.values.lowestNo ||
                filter.values.highestNo;

          if (!isOperatorValid && isValuesValid) {
            operatorError = "Operator required.";
          }

          if (isOperatorValid && !isValuesValid) {
            valuesError = "Values required.";
          }
        }

        return { ...filter, valuesError, operatorError };
      });

      setFilterList(updatedFilterList);

      const isValid = !updatedFilterList.some((filter) => {
        if (filter.operatorError || filter.valuesError) {
          return true;
        }
        return false;
      });

      resolve(isValid);
    });
  };

  // Submit & Reset
  const onSubmitAdvanceSearch = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        setSubmitLoading(true);
        const updatedFilter = convertToFilters(filterList);

        const payload = {
          search: {
            searchText: searchText,
            searchType: advanceSearchType,
          },
          matchText: matchValue,
          match: isAllMatched,
          filters: updatedFilter,
          skip: 0,
          take: DEFAULT_PAGE_SIZE,
        };
        const response = await newAuthService(
          getGlobalASFilterResultUrl,
          "POST",
          payload
        );
        if (response.status) {
          const serachObject = {
            search: {
              searchText: searchText,
              searchType: advanceSearchType,
            },
            filters: updatedFilter,
            filterList,
            matchText: matchValue,
            match: isAllMatched,
          };
          localStorage.setItem("serachObject", JSON.stringify(serachObject));
          triggerGlobalSearchReset();
          navigate(`${paths.common.searchResult}?ads=1`);
          setAdvanceSearchDropdown(false);
          setSubmitLoading(false);
        } else {
          toast.error(t("common.messages.notification.failed.serverError"));
          setSubmitLoading(false);
        }
      } catch {
        toast.error(t("common.messages.notification.failed.serverError"));
        setSubmitLoading(false);
      }
    }
  };

  const onResetAdvanceSearch = () => {
    setSearchText("");
    setAdvanceSearchType("");
    setFilterList([]);
    setSearchList([]);
    localStorage.removeItem("serachObject");
    setAdvanceSearchDropdown(false);
  };

  // Redirect
  const {
    admin: {
      applicationStatus: { root: applicationStatus, overview },
    },
  } = paths;

  const handleGoToSerachResult = () => {
    navigate(`${paths.common.searchResult}?serachText=${searchText}`);
  };

  const handleGoToApplicant = (people) => {
    navigate(`${applicationStatus}/${people.gid}${overview}`);
  };

  // Render
  const renderSearchIcon = (searchName) => {
    if (searchName === "Name") {
      return <i className="bx bx-user bx-xs"></i>;
    }
    if (searchName === "University") {
      return <i className="bx bx-user bx-xs"></i>;
    }
    if (searchName === "Project Name") {
      return <i className="bx bx-folder-open bx-xs"></i>;
    }
    if (searchName === "Email") {
      return <i className="bx bx-send bx-xs"></i>;
    }
    if (searchName === "Application Number") {
      return <i className="bx bx-xs">123</i>;
    }
    if (searchName === "DOB") {
      return <i className="bx bx-calendar-check bx-xs"></i>;
    }
  };

  const renderFilterType = (filter) => {
    const {
      type: filterType,
      id: filterId,
      dropdownValue: dropdownValues,
      values,
    } = filter;
    if (filterType === "date") {
      if (filter.operator === "betweenrange") {
        return (
          <>
            <input
              type="date"
              className="form-control px-3"
              id={filterId}
              value={values?.startDate || ""}
              onChange={(e) =>
                handleFilterDateChange(
                  e.target.value,
                  values?.endDate || "",
                  filterId
                )
              }
            />
            <input
              type="date"
              className="form-control px-3"
              id={filterId}
              value={values?.endDate || ""}
              min={disablePastDate(values?.startDate)}
              onChange={(e) =>
                handleFilterDateChange(
                  values?.startDate || "",
                  e.target.value,
                  filterId
                )
              }
            />
          </>
        );
      }
      return (
        <input
          type="date"
          className="form-control px-3"
          id={filterId}
          value={values?.startDate || ""}
          onChange={(e) => handleFilterDateChange(e.target.value, "", filterId)}
        />
      );
    }
    if (filterType === "number") {
      if (filter.operator === "betweenrange") {
        const minValue = Number(dropdownValues?.[0] ?? 0);
        const maxValue = Number(dropdownValues?.[1] ?? 100);
        return (
          <DualRangeSlider
            min={minValue}
            max={maxValue}
            values={[
              Number(values?.lowestNo) || minValue,
              Number(values?.highestNo) || maxValue,
            ]}
            onChange={(value) =>
              handleFilterSliderChange(value, filterId, filter.gid)
            }
          />
        );
      }
      return (
        <input
          type="number"
          className="form-control px-3"
          id={filterId}
          value={values?.lowestNo || ""}
          min={1}
          onChange={(e) =>
            handleFilterSliderChange(
              [e.target.value, undefined],
              filterId,
              filter.gid
            )
          }
        />
      );
    }
    if (filterType === "select") {
      return (
        <select
          className="form-select"
          aria-label="Default select example"
          value={values || ""}
          onChange={(e) => handleFilterSelectChange(e, filterId)}
        >
          <option value="">Select</option>
          {dropdownValues &&
            dropdownValues.length > 0 &&
            dropdownValues.map((option, optionIndex) => (
              <option value={option} key={optionIndex.toString()}>
                {option}
              </option>
            ))}
        </select>
      );
    }
    if (filterType === "multiselect") {
      const dataList =
        dropdownValues &&
        dropdownValues.length > 0 &&
        dropdownValues.map((option) => ({
          value: option,
          label: option,
        }));
      return (
        <MultiCheckDropdown
          options={dataList}
          onChange={(values) => handleFilterMultiSelectChange(values, filterId)}
          placeholder={t("common.placeHolder.search")}
          className="form-control px-3"
        />
      );
    }
    return (
      <input
        type="text"
        className="form-control px-3"
        id={filterId}
        value={values || ""}
        onChange={(e) => handleFilterTextChange(e, filterId)}
      />
    );
  };

  return (
    <>
      <header
        className={
          isSiderbarToggle ? "header bg-grey-50" : "header body-pd bg-grey-50"
        }
        id="header"
      >
        <button type="button" className="header_toggle" onClick={toggleSidebar}>
          <i className="bx bx-menu" id="header-toggle"></i>
        </button>
        <div className="app-header-left d-sm-flex align-items-center w-100 ms-sm-3 column-gap-3">
          <div className="admin-div">
            <span className="admin-name d-block text-md font-bold leading-none">
              {t("common.hi")} {user && user.firstName}
            </span>
            <small className="status d=block text-xs">{message}!</small>
          </div>
          <div className="search-div flex-auto mx-auto">
            <div className="pt-sm-1 pb-sm-1">
              <div className="advance-search-wrapper" ref={searchDropdownRef}>
                <div className="advance-search-header">
                  <div className="input-group rounded-lg bg-white">
                    <span className="btn px-2 py-2 border border-end-0 border-slate-200 text-slate-400 d-flex align-items-center">
                      <i className="bx bx-search bx-xs"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 border-end-0 ps-0"
                      placeholder={t("common.placeHolder.search")}
                      name="searchText"
                      value={searchText}
                      onChange={handleSearchChange}
                    />

                    {user.type === USER_TYPE.Admin ? (
                      <button
                        className={`btn px-2 py-2 border border-start-0 border-slate-200 text-slate-400 ${
                          isAdvanceSearchApplied ? "active" : ""
                        }`}
                        type="button"
                        onClick={showAdvanceSearchDropdown}
                      >
                        <i className="bx bx-slider bx-xs"></i>
                      </button>
                    ) : null}
                  </div>
                </div>
                {globalSearchDropdown && searchResult && (
                  <div className="search-dropdown py-3 ps-3 pe-2  rounded-xl">
                    <div className="overflow-y-auto max-h-300 d-flex flex-column row-gap-2 pe-1">
                      {searchResult.trendingSearch &&
                        searchResult.trendingSearch.length > 0 && (
                          <div className="search-dropdown-box">
                            <div className="search-title">Trending Search</div>
                            <ul className="search-category">
                              {searchResult.trendingSearch.map(
                                (trending, index) => (
                                  <li key={index}>
                                    <a href="/">{trending.topic}</a>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      {searchResult.recentSearch &&
                        searchResult.recentSearch.length > 0 && (
                          <div className="search-dropdown-box">
                            <div className="search-title">Recent Search</div>

                            <ul className="search-list">
                              {searchResult.recentSearch.map(
                                (recent, index) => (
                                  <li key={index}>
                                    <a
                                      href="/"
                                      className="px-3 py-2 rounded-lg"
                                    >
                                      <i className="bx bx-time-five"></i>
                                      {recent.recentSeaarchText}
                                    </a>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      {searchResult.peopleResult &&
                        searchResult.peopleResult.length > 0 && (
                          <>
                            <div className="search-dropdown-box mb-0">
                              <ul className="user-search-list d-flex flex-column">
                                {searchResult.peopleResult.map(
                                  (people, index) => (
                                    <li key={index}>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGoToApplicant(people)
                                        }
                                        className="w-100 border-0 bg-transparent px-3 py-2 rounded-lg d-flex align-items-center"
                                      >
                                        <div className="search-userimg overflow-hidden w-7 h-7">
                                          {people.photoURL ? (
                                            <img
                                              src={people.photoURL}
                                              alt={`${people.firstName}${people.lastName}`}
                                              className="w-100 h-100 object-fit-cover"
                                            />
                                          ) : (
                                            getFirstLetterOfName(people)
                                          )}
                                        </div>
                                        <div className="search-userdetails text-start ps-2 text-grey-300">
                                          <p className="mb-0 leading-none">
                                            {people.firstName} {people.lastName}
                                          </p>
                                          <div className="d-flex flex-wrap align-items-center gap-1 text-xxs text-grey-400">
                                            <span className="search-sub-content">
                                              {people.type}
                                            </span>{" "}
                                            |{" "}
                                            <span className="search-sub-content">
                                              {people.program}
                                            </span>
                                          </div>
                                        </div>
                                        <i className="bx bx-search bx-xs ms-auto"></i>
                                      </button>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </>
                        )}
                    </div>
                    {searchResult.trendingSearch?.length > 0 ||
                    searchResult.recentSearch?.length > 0 ||
                    searchResult.peopleResult?.length > 0 ? (
                      <button
                        type="button"
                        className="dropdown-search-btn font-medium text-grey-600"
                        onClick={handleGoToSerachResult}
                      >
                        {t("common.button.seeAllResults")}
                      </button>
                    ) : (
                      <div className="text-center py-3">
                        {t("common.messages.error.notFound.noResultsFound")}
                      </div>
                    )}
                  </div>
                )}
                {advanceSearchDropdown && (
                  <div className="advance-search-body pt-3 position-absolute start-0 end-0 bg-white overflow-hidden rounded-bottom">
                    <div className="d-flex flex-column align-items-start gap-3">
                      <div className="d-flex flex-column align-items-start gap-3 mob-advance-search w-100">
                        <div className="w-100 px-4">
                          <h4 className="search-title text-sm font-normal text-blue-600">
                            {t("common.label.searchBy")}
                          </h4>
                          <ul className="mb-0 d-flex flex-wrap gap-1 list-unstyled search-by-list">
                            {searchLoading ? (
                              <li className="text-center w-100">
                                <LoadingScreen />
                              </li>
                            ) : (
                              searchList &&
                              searchList.length > 0 &&
                              searchList.map((search, index) => (
                                <li
                                  key={index.toString()}
                                  onClick={() =>
                                    handleAdvanceSearchType(search.gid)
                                  }
                                >
                                  <span
                                    className={`px-2 py-1 d-flex align-items-center gap-1 text-xs font-medium text-slate-600 border border-slate-200 rounded bg-slate-50 cursor-pointer serachby-tag ${
                                      advanceSearchType === search.gid
                                        ? "active"
                                        : ""
                                    }`}
                                  >
                                    {renderSearchIcon(search.name)}{" "}
                                    {search.name}
                                  </span>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                        <hr className="w-100 my-0 border-slate-400" />
                        <div className="w-100 d-flex flex-column">
                          <h4 className="search-title text-sm font-normal text-blue-600 px-4">
                            {t("common.label.filterBy")}
                          </h4>
                          <div className="d-flex align-items-center gap-3 px-4 mb-2">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="checkDefault"
                                checked={isAllMatched}
                                onChange={handleIsAllMatchedChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="checkDefault"
                              >
                                {t("common.label.match")}
                              </label>
                            </div>
                            <select
                              className="form-select w-20"
                              aria-label="Default select example"
                              value={matchValue}
                              onChange={handleMatchValueChange}
                            >
                              <option value="All">
                                {t("common.label.all")}
                              </option>
                              <option value="Any">
                                {t("common.label.any")}
                              </option>
                            </select>
                            <span>
                              {t("common.placeHolder.ofTheFollowingRules")}:
                            </span>
                          </div>
                          <div className="d-flex flex-column row-gap-2 px-4">
                            {filterLoading ? (
                              <div className="text-center filter-loader-height d-flex justify-content-center align-items-center">
                                <LoadingScreen />
                              </div>
                            ) : (
                              filterList &&
                              filterList.length > 0 &&
                              filterList.map((filter, filterIndex) => (
                                <div
                                  className="filter-row"
                                  key={filterIndex.toString()}
                                >
                                  <div className="row g-1 mx-0">
                                    <div className="col-xxl-12 col-xl-12 col-lg-12 col-sm-12 col-12">
                                      <label
                                        htmlFor=""
                                        className="form-label font-medium text-grey-500 mb-0 leading-tight"
                                      >
                                        {filter.fieldName}
                                      </label>
                                    </div>
                                    {filter.operatorValue &&
                                      filter.operatorValue.length > 0 && (
                                        <div className="col-xxl-4 col-xl-3 col-lg-4 col-sm-12 col-12">
                                          <select
                                            className="form-select"
                                            aria-label="Default select example"
                                            value={filter.operator}
                                            onChange={(e) =>
                                              handleFilterOperatorChange(
                                                e,
                                                filter.id
                                              )
                                            }
                                          >
                                            <option value="">Select</option>
                                            {filter.operatorValue.map(
                                              (operator, operatorIndex) => (
                                                <option
                                                  value={operator.value}
                                                  key={operatorIndex.toString()}
                                                >
                                                  {operator.name}
                                                </option>
                                              )
                                            )}
                                          </select>
                                          {filter.operatorError && (
                                            <p className="error">
                                              {filter.operatorError}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    <div className="col-xxl-8 col-xl-9 col-lg-8 col-sm-12 col-12">
                                      <div
                                        className={`d-flex flex-lg-nowrap flex-wrap gap-1 filter-row ${
                                          !filter.isPredefined
                                            ? "loop-row-field"
                                            : ""
                                        }`}
                                      >
                                        {renderFilterType(filter)}
                                        {filter.valuesError && (
                                          <p className="error">
                                            {filter.valuesError}
                                          </p>
                                        )}

                                        {!filter.isPredefined && (
                                          <div className="d-flex gap-2 top-0 end-0 h-100 filter-right-loop-btn">
                                            <button
                                              type="button"
                                              className="btn text-red-400 border border-red-100 bg-red-50 p-2 w-9 d-flex jutify-content-center align-items-center h-100"
                                              onClick={() =>
                                                handleRemoveFilter(filter.id)
                                              }
                                            >
                                              <i className="bx bx-x bx-xs"></i>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                      <hr className="w-100 my-0 border-slate-400" />
                      <div className="w-100">
                        <div className="d-flex flex-wrap align-items-center gap-2 px-4 pb-4 result-row">
                          <div className="me-auto result-w-lg">
                            <Dropdown>
                              {!customFilterLoading ? (
                                <Dropdown.Toggle
                                  disabled={customFilterList.length === 0}
                                >
                                  {t("common.label.addMoreFilter")}
                                </Dropdown.Toggle>
                              ) : (
                                <ButtonLoader />
                              )}

                              <Dropdown.Menu className="w-60">
                                <Dropdown.Header className="px-3">
                                  <input
                                    type="search"
                                    className="form-control px-2"
                                    placeholder={t("common.placeHolder.search")}
                                    value={customFilterSearchText}
                                    onChange={handleCustomFilterSearch}
                                  />
                                </Dropdown.Header>
                                <div className="max-h-200 overflow-y-auto">
                                  {customFilterListWithSearch &&
                                    customFilterListWithSearch.length > 0 &&
                                    customFilterListWithSearch.map(
                                      (filter, filterIndex) => (
                                        <Dropdown.Item
                                          role="button"
                                          key={filterIndex.toString()}
                                          onClick={() =>
                                            handleChangeCustomFilter(filter)
                                          }
                                          className="px-3 text-grey-600"
                                        >
                                          {filter.fieldName}
                                        </Dropdown.Item>
                                      )
                                    )}
                                </div>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                          <div className="d-flex align-items-center gap-1 ml-auto result-w-lg result-btn-row">
                            <button
                              type="button"
                              className="btn btn-gray-light"
                              onClick={onResetAdvanceSearch}
                            >
                              {t("common.button.reset")}
                            </button>
                            {!submitLoading ? (
                              <button
                                type="button"
                                className="btn btn-primary text-nowrap"
                                onClick={onSubmitAdvanceSearch}
                              >
                                {t("common.button.applyFilter")}
                              </button>
                            ) : (
                              <ButtonLoader />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="search-overlay"></div>
        </div>
        <div className="header-admin-box">
          <div
            className="dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {user && (
              <>
                <div className="admin-img">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={`${user.firstName}${user.lastName}`}
                    />
                  ) : (
                    getFirstLetterOfName(user)
                  )}
                </div>
                <div className="admin-info ms-2">
                  <h6>
                    {user.firstName} {user.lastName}
                  </h6>
                  <small>{user.type}</small>
                </div>
              </>
            )}
          </div>
          <ul className="dropdown-menu">
            <li>
              <Link className="dropdown-item" to={paths.common.viewProfile}>
                <i className="bx bx-cog"></i> {t("common.button.viewProfile")}
              </Link>
            </li>
            <li className="dropdown-logout">
              <button className="btn btn-secondary btn-block" onClick={Logout}>
                {t("common.button.signOut")}
              </button>
            </li>
          </ul>
        </div>
        <div className="app-header-right ">
          <ul className="list-unstyled m-0 d-flex align-items-center column-gap-1">
            <li>
              <Dropdown
                show={privateNoteDropdown}
                onToggle={togglePrivateNoteDropdown}
              >
                <Dropdown.Toggle className="btn btn-transparent border-0 position-relative w-10 h-10 d-flex justify-content-center align-items-center dropdown-arrow-none dropdown-toggle">
                  {/* <span className="notification-badge bg-red-500 w-4 h-4 d-flex justify-content-center align-items-center text-white rounded-circle position-absolute right-0">
                    <small>0</small>
                  </span> */}
                  <span className="material-symbols-outlined">
                    <i className="pluto-edit-note"></i>
                  </span>
                  {/* <i className="pluto-edit-note"></i> */}
                </Dropdown.Toggle>
                <Dropdown.Menu className="dropdown-menu dropdown-menu-lg-end p-3 w-360 dropdown-full-h border-0 shadow overflow-y-auto">
                  <div className="overflow-y-auto d-flex flex-column gap-2">
                    <div className="dropdown-title text-md font-medium leading-none text-slate-500">
                      Private Note
                    </div>
                    <PrivateNotes toggle={togglePrivateNoteDropdown} />
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </li>
            {totalNotification >= 0 && (
              <li>
                <Dropdown
                  show={notificationDropdown}
                  onToggle={toggleNotificationDropdown}
                >
                  <Dropdown.Toggle className="btn btn-transparent border-0 position-relative w-10 h-10 d-flex justify-content-center align-items-center dropdown-arrow-none">
                    {totalNotification > 0 && (
                      <span className="notification-badge bg-red-500 w-4 h-4 d-flex justify-content-center align-items-center text-white rounded-circle position-absolute right-0">
                        <small className="text-xxs">{totalNotification}</small>
                      </span>
                    )}
                    <span className="material-symbols-outlined">
                      notifications
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dropdown-menu-lg-end p-3 w-360 dropdown-full-h border-0 shadow">
                    <div className="overflow-y-auto d-flex flex-column gap-2">
                      <div className="dropdown-title text-md font-medium leading-none text-slate-500">
                        Notification
                      </div>
                      <Notifications toggle={toggleNotificationDropdown} />
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </li>
            )}
            <li>
              <div className="dropdown">
                <button
                  className="py-0 pe-0 btn btn-transparent border-0 position-relative d-flex justify-content-center align-items-center dropdown-arrow-none dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className="me-auto d-flex flex-column text-end">
                      <div className="text-sm font-bold text-black leading-none">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <span className="text-xs leading-none">{user?.type}</span>
                    </div>

                    <div className="w-10 h-10 d-flex justify-content-center align-items-center rounded-circle border border-grey-200 overflow-hidden">
                      {user?.photoURL ? (
                        <img
                          src={user?.photoURL}
                          alt={`${user?.firstName}${user?.lastName}`}
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        getFirstLetterOfName(user)
                      )}
                    </div>
                  </div>
                </button>
                <ul className="dropdown-menu w-64">
                  <li>
                    <Link
                      className="dropdown-item py-2 text-slate-600"
                      to={paths.common.viewProfile}
                    >
                      <i className="bx bx-cog"></i>{" "}
                      {t("common.button.viewProfile")}
                    </Link>
                  </li>
                  <li className="dropdown-logout mt-3 px-3">
                    <button
                      className="btn btn-secondary btn-block w-100"
                      onClick={Logout}
                    >
                      {t("common.button.signOut")}
                    </button>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </header>
    </>
  );
};

export default PrivateHeader;
